import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
	ActiveProductService,
	AuthenticationService,
	ConsolidatedLoginBenefits,
	EducationPathway,
	ParticipantService,
	Product,
	ProductType,
	User
} from '@edassist/api';
import {
	constants,
	GoogleTagManagerEventService,
	HybridAppService,
	LocalStorageService,
	ParticipantUtilityService,
	PRODUCT_TYPES
} from '@edassist/common';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BUTTON_TYPES } from '@edassist-ui/button';
import { DropdownNavItemComponent } from '@edassist-ui/dropdown-nav-item';
import { DropdownService } from '@edassist-ui/dropdown';

@Component({
	selector: 'tu-other-services',
	templateUrl: './other-services.component.html',
	styleUrls: ['./other-services.component.scss']
})
export class OtherServicesComponent implements OnInit, OnDestroy {
	isShowProductDropdown: boolean;
	productList: Array<ProductType>;
	pathwayList: Array<EducationPathway>;
	constants = constants;
	participantId: number;
	activeProduct: number;
	activePathwayId: number;
	consolidatedLoginBenefits: Array<ConsolidatedLoginBenefits>;
	BUTTON_TYPES = BUTTON_TYPES;
	@ViewChild('dropdownContentNav')
	dropdownContentNav?: DropdownNavItemComponent;

	private readonly subscriptions = new Subscription();
	constructor(
		private readonly authService: AuthenticationService,
		private readonly participantService: ParticipantService,
		private readonly activeProductService: ActiveProductService,
		private readonly hybridAppService: HybridAppService,
		private readonly gtmEventService: GoogleTagManagerEventService,
		private readonly dropdownService: DropdownService
	) {}

	ngOnInit(): void {
		this.subscriptions.add(
			this.authService.currentUser.subscribe((user: User) => {
				this.participantId = user?.participantId;
				this.productList =
					user?.productTypes?.filter(
						(item) => item.id !== PRODUCT_TYPES.educationPathways
					) || [];
				this.pathwayList = user?.pathwayList || [];
				this.consolidatedLoginBenefits =
					ParticipantUtilityService.getConsolidatedLoginBenefits() || [];
				this.isShowProductDropdown =
					this.productList?.length > 1 ||
					!!this.consolidatedLoginBenefits?.length ||
					this.pathwayList?.length > 1 ||
					(this.productList?.length > 0 && this.pathwayList?.length > 0);
			})
		);
		this.subscriptions.add(
			this.activeProductService.activeProduct.subscribe((product: Product) => {
				this.activeProduct = product?.id;
			})
		);
		this.activeProduct = this.activeProductService.getActiveProduct()?.id;
	}

	onSetActivePathway(pathway: EducationPathway): void {
		LocalStorageService.setItem(
			constants.localStorageKeys.USER_ACTIVE_PATHWAY,
			pathway
		);
		this.activePathwayId = pathway?.id;
		this.activeProductService.updateActiveProduct({
			id: PRODUCT_TYPES.educationPathways,
			name: this.authService.currentUserValue?.productTypes?.find(
				(productType) => productType.id === PRODUCT_TYPES.educationPathways
			)?.name
		});
		this.onProductSelect();
	}

	onProductSelect(): void {
		this.participantService
			.getParticipantEligibility(
				this.participantId,
				this.activeProductService.getActiveProduct().id
			)
			.pipe(
				finalize(() => {
					this.navigateToHomePage();
				})
			)
			.subscribe();
	}

	onCustomProductSelect(): void {
		this.navigateToHomePage();
	}

	onSetActiveProduct(productId: number): void {
		if (
			this.activeProductService.getActiveProduct().id === PRODUCT_TYPES.tuition
		) {
			this.activePathwayId = undefined;
		}
		this.activeProductService.updateActiveProduct({
			id: productId,
			name: this.authService.currentUserValue?.productTypes?.find(
				(productType) => productType.id === productId
			)?.name
		});
		this.dropdownService.close(constants.dropdownIds.otherServices);
		productId === PRODUCT_TYPES.customPathway ?
			this.onCustomProductSelect()
		:	this.onProductSelect();
	}

	navigateToHomePage(): void {
		let loc = '';
		if (!this.productList.length) {
			this.authService.hybridAppLogout();

			return;
		}
		if (this.productList.length === 1 && !this.activePathwayId) {
			this.activeProduct = this.productList[0].id;
			this.activeProductService.updateActiveProduct({
				id: this.productList[0].id,
				name: this.authService.currentUserValue?.productTypes?.find(
					(productType) => productType.id === this.productList[0].id
				)?.name
			});
		}

		switch (this.activeProduct) {
			case PRODUCT_TYPES.tuition:
				loc = constants.BASE_APP_ROUTES.HOME;
				break;
			case PRODUCT_TYPES.loan:
				loc = constants.routes.loanRepay.dashboard;
				break;
			case PRODUCT_TYPES.expertGuidance:
				loc = constants.routes.advising;
				break;
			case PRODUCT_TYPES.educationPathways:
				loc = constants.BASE_APP_ROUTES.HOME;
				break;
			case PRODUCT_TYPES.customPathway:
				loc = constants.routes.CLIENT_CUSTOM_PATHWAY;
				break;
			default:
				loc = constants.routes.login;
				break;
		}

		// Send cta_click event to GTM
		this.gtmEventService.sendCtaClick(
			this.activeProductService.getActiveProduct()?.name,
			loc,
			constants.googleAnalytics.callToAction.pageSection.header
		);

		if (loc === constants.routes.CLIENT_CUSTOM_PATHWAY) {
			const user: User = LocalStorageService.getItem(
				constants.localStorageKeys.CURRENT_USER_HYBRID
			).userDetails;
			this.authService.setUserConfigs(user);
			this.hybridAppService.navigateToClientCustomPathwayPage();
		} else {
			this.hybridAppService.navigateToUrl(loc);
		}
	}

	handleDropdownOnKeydown(
		isLastProduct: boolean,
		isLastPathway: boolean,
		islastOtherBenefit: boolean
	): void {
		if (
			isLastProduct &&
			this.pathwayList.length === 0 &&
			this.consolidatedLoginBenefits.length === 0
		) {
			this.dropdownContentNav.toggleDropDown();
		} else if (isLastPathway && this.consolidatedLoginBenefits.length === 0) {
			this.dropdownContentNav.toggleDropDown();
		} else if (islastOtherBenefit) {
			this.dropdownContentNav.toggleDropDown();
		}
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
